import { useEffect } from "react";
import { beginEscapeGuard, endEscapeGuard } from "@/lib/escapeGuard";
import { Check, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { InfoCard } from "@/components/workOrderTab/WOGeneralCard";

// "Sites" card on the customer Info tab — every site, ★ marks the primary (billing / main office; drives the
// address shown). Click a row to switch the whole drawer to that site; All Sites shows the customer-level view.
export function ClientSitesCard({
  clientSites, selectedSiteId, switchSiteData, handleSetPrimary,
  renamingSite, setRenamingSite, siteRenameValue, setSiteRenameValue, handleRenameSite,
  setIsSiteDialogOpen, setDeleteSiteConfirmOpen,
}) {
  useEffect(() => { if (!renamingSite) return undefined; beginEscapeGuard(); return endEscapeGuard; }, [renamingSite]);
  const sites = [...clientSites].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) || (a.name || "").localeCompare(b.name || ""));
  const rowCls = (on) => `group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors ${on ? "bg-brand/10 text-brand font-semibold" : "text-slate-700 hover:bg-slate-100"}`;

  return (
    <InfoCard title="Sites" testId="client-sites-card" className="h-full"
      action={(
        <button type="button" onClick={() => setIsSiteDialogOpen(true)} title="Add a site" data-testid="btn-add-site"
          className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}>
      <div className="space-y-0.5 max-h-[340px] overflow-y-auto pr-1" data-testid="client-sites-list">
        {sites.length > 1 && (
          <div className={rowCls(selectedSiteId === "all")} onClick={() => switchSiteData("all")} data-testid="site-row-all">
            <span className="w-4" />
            <span className="flex-1">All sites</span>
            <span className="text-xs text-slate-400 num">{sites.length}</span>
          </div>
        )}
        {sites.map((s) => {
          const on = selectedSiteId === s.id;
          const place = [s.city, s.state].filter(Boolean).join(", ");
          return (
            <div key={s.id} className={rowCls(on)} onClick={() => switchSiteData(s.id)} data-testid={`site-row-${s.id}`}>
              <button type="button" title={s.is_primary ? "Primary site (billing / main office)" : "Make this the primary site"}
                onClick={(e) => { e.stopPropagation(); if (!s.is_primary) handleSetPrimary(s.id); }} data-testid={`site-primary-${s.id}`}
                className={`shrink-0 ${s.is_primary ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}>
                <Star className={`w-3.5 h-3.5 ${s.is_primary ? "fill-current" : ""}`} />
              </button>
              {on && renamingSite ? (
                <span className="flex flex-1 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Input value={siteRenameValue} onChange={(e) => setSiteRenameValue(e.target.value)} autoFocus className="h-7 text-sm" data-testid="input-rename-site"
                    onKeyDown={(e) => { if (e.key === "Enter") handleRenameSite(); if (e.key === "Escape") setRenamingSite(false); }} />
                  <button type="button" className="text-emerald-600" onClick={handleRenameSite} title="Save" data-testid="btn-confirm-rename"><Check className="w-4 h-4" /></button>
                  <button type="button" className="text-slate-400" onClick={() => setRenamingSite(false)} title="Cancel"><X className="w-4 h-4" /></button>
                </span>
              ) : (
                <>
                  <span className="flex-1 min-w-0 truncate">{s.name || "Unnamed site"}</span>
                  {place && <span className="text-xs text-slate-400 truncate max-w-[45%]">{place}</span>}
                  <span className="hidden group-hover:flex items-center gap-1 shrink-0">
                    <button type="button" title="Rename site" data-testid={`btn-rename-site-${s.id}`} className="text-slate-400 hover:text-slate-700"
                      onClick={(e) => { e.stopPropagation(); switchSiteData(s.id); setSiteRenameValue(s.name || ""); setRenamingSite(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {sites.length > 1 && (
                      <button type="button" title="Delete site" data-testid={`btn-delete-site-${s.id}`} className="text-slate-400 hover:text-red-500"
                        onClick={(e) => { e.stopPropagation(); switchSiteData(s.id); setDeleteSiteConfirmOpen(true); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </span>
                </>
              )}
            </div>
          );
        })}
        {sites.length === 0 && <div className="text-xs text-slate-400">No sites yet — add the first one with the + button.</div>}
      </div>
    </InfoCard>
  );
}
